/**
 * Auscultation placement points, mirrored 1:1 from the Android
 * `StethoScopeActivity` companion arrays.
 *
 * Coordinates are in the *bitmap* pixel space of `placement_body_front.png`
 * (634x640) and `placement_body_back.png` (617x640). The placement guide
 * component rescales them to the displayed image size.
 */

export const FRONT_IMAGE_SIZE = Object.freeze({ w: 634, h: 640 });
export const BACK_IMAGE_SIZE = Object.freeze({ w: 617, h: 640 });

export const FRONT_POINTS = Object.freeze([
	{
		id: 'aortic',
		label: 'Aortic Area',
		side: 'front',
		color: '#E53935',
		instruction: 'Place at the right 2nd intercostal space near the sternal border.',
		bitmapX: 232,
		bitmapY: 156,
	},
	{
		id: 'pulmonic',
		label: 'Pulmonic Area',
		side: 'front',
		color: '#1565C0',
		instruction: 'Place at the left 2nd intercostal space near the sternal border.',
		bitmapX: 399,
		bitmapY: 156,
	},
	{
		id: 'erbs_point',
		label: 'Erb\'s Point',
		side: 'front',
		color: '#43A047',
		instruction: 'Place at the left 3rd intercostal space near the sternal border.',
		bitmapX: 312,
		bitmapY: 218,
	},
	{
		id: 'tricuspid',
		label: 'Tricuspid Area',
		side: 'front',
		color: '#FB8C00',
		instruction: 'Place at the left lower sternal border around the 4th intercostal space.',
		bitmapX: 395,
		bitmapY: 271,
	},
	{
		id: 'mitral',
		label: 'Mitral (Apex)',
		side: 'front',
		color: '#8E24AA',
		instruction: 'Place at the cardiac apex, left 5th intercostal space near the midclavicular line.',
		bitmapX: 453,
		bitmapY: 307,
	},
	{
		id: 'ruq',
		label: 'RUQ',
		side: 'front',
		color: '#00ACC1',
		instruction: 'Place gently over the right upper abdominal quadrant.',
		bitmapX: 217,
		bitmapY: 404,
	},
	{
		id: 'luq',
		label: 'LUQ',
		side: 'front',
		color: '#EC407A',
		instruction: 'Place gently over the left upper abdominal quadrant.',
		bitmapX: 409,
		bitmapY: 404,
	},
	{
		id: 'rlq',
		label: 'RLQ',
		side: 'front',
		color: '#FBC02D',
		instruction: 'Place gently over the right lower abdominal quadrant.',
		bitmapX: 216,
		bitmapY: 523,
	},
	{
		id: 'llq',
		label: 'LLQ',
		side: 'front',
		color: '#0D47A1',
		instruction: 'Place gently over the left lower abdominal quadrant.',
		bitmapX: 408,
		bitmapY: 523,
	},
]);

export const BACK_POINTS = Object.freeze([
	{
		id: 'upper_left_lobe',
		label: 'Upper Left Lobe',
		side: 'back',
		color: '#43A047',
		instruction: 'Place the stethoscope over the upper left lung area below the scapula.',
		bitmapX: 234,
		bitmapY: 133,
	},
	{
		id: 'upper_right_lobe',
		label: 'Upper Right Lobe',
		side: 'back',
		color: '#E53935',
		instruction: 'Place the stethoscope over the upper right lung area below the scapula.',
		bitmapX: 377,
		bitmapY: 133,
	},
	{
		id: 'upper_middle_left',
		label: 'Upper-Middle Left',
		side: 'back',
		color: '#2962FF',
		instruction: 'Place between the left scapula and spine for upper-middle posterior lung sounds.',
		bitmapX: 234,
		bitmapY: 192,
	},
	{
		id: 'upper_middle_right',
		label: 'Upper-Middle Right',
		side: 'back',
		color: '#FF8F00',
		instruction: 'Place between the right scapula and spine for upper-middle posterior lung sounds.',
		bitmapX: 377,
		bitmapY: 192,
	},
	{
		id: 'middle_left',
		label: 'Middle Left',
		side: 'back',
		color: '#7B1FA2',
		instruction: 'Place over the middle left posterior lung area.',
		bitmapX: 234,
		bitmapY: 250,
	},
	{
		id: 'middle_right',
		label: 'Middle Right',
		side: 'back',
		color: '#00ACC1',
		instruction: 'Place over the middle right posterior lung area.',
		bitmapX: 377,
		bitmapY: 250,
	},
	{
		id: 'lower_left',
		label: 'Lower Left',
		side: 'back',
		color: '#FBC02D',
		instruction: 'Place over the lower left posterior lung field.',
		bitmapX: 210,
		bitmapY: 325,
	},
	{
		id: 'lower_right',
		label: 'Lower Right',
		side: 'back',
		color: '#FF7043',
		instruction: 'Place over the lower right posterior lung field.',
		bitmapX: 400,
		bitmapY: 325,
	},
	{
		id: 'lower_mid_left',
		label: 'Lower-Mid Left',
		side: 'back',
		color: '#445696',
		instruction: 'Place over the lower-mid left posterior lung field, just below the lower lobe.',
		bitmapX: 214,
		bitmapY: 385,
	},
	{
		id: 'lower_mid_right',
		label: 'Lower-Mid Right',
		side: 'back',
		color: '#7C4423',
		instruction: 'Place over the lower-mid right posterior lung field, just below the lower lobe.',
		bitmapX: 398,
		bitmapY: 385,
	},
	{
		id: 'lower_inferior_left',
		label: 'Lower Inferior Left',
		side: 'back',
		color: '#AEEA00',
		instruction: 'Place near the lower inferior left lung base.',
		bitmapX: 223,
		bitmapY: 452,
	},
	{
		id: 'lower_inferior_right',
		label: 'Lower Inferior Right',
		side: 'back',
		color: '#64FFDA',
		instruction: 'Place near the lower inferior right lung base.',
		bitmapX: 388,
		bitmapY: 452,
	},
	{
		id: 'lowest_left',
		label: 'Lowest Left',
		side: 'back',
		color: '#311B92',
		instruction: 'Place at the lowest left posterior lung base.',
		bitmapX: 228,
		bitmapY: 513,
	},
	{
		id: 'lowest_right',
		label: 'Lowest Right',
		side: 'back',
		color: '#4A148C',
		instruction: 'Place at the lowest right posterior lung base.',
		bitmapX: 384,
		bitmapY: 513,
	},
]);

/**
 * @param {string | undefined} id
 * @param {string | undefined} side
 */
export function findPlacement (id, side) {
	if (!id) return null;
	const list = side === 'back' ? BACK_POINTS : side === 'front' ? FRONT_POINTS : [...FRONT_POINTS, ...BACK_POINTS];
	return list.find((p) => p.id === id) ?? null;
}
